import { Capacitor } from '@capacitor/core';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';

const BIOMETRIC_ENABLED_KEY = 'qureo.biometricEnabled';
const BIOMETRIC_SESSION_UNLOCKED_KEY = 'qureo.biometricSessionUnlocked';

export function isBiometricUnlockEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
}

export function setBiometricUnlockEnabled(enabled) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(BIOMETRIC_ENABLED_KEY, enabled ? 'true' : 'false');
  if (!enabled) {
    window.localStorage.removeItem(BIOMETRIC_SESSION_UNLOCKED_KEY);
  }
}

export function isBiometricSessionUnlocked() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return true;
  }

  return window.sessionStorage.getItem(BIOMETRIC_SESSION_UNLOCKED_KEY) === 'true';
}

export function setBiometricSessionUnlocked(unlocked) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  if (unlocked) {
    window.sessionStorage.setItem(BIOMETRIC_SESSION_UNLOCKED_KEY, 'true');
    return;
  }

  window.sessionStorage.removeItem(BIOMETRIC_SESSION_UNLOCKED_KEY);
}

export function canUseNativeBiometrics() {
  return Capacitor.isNativePlatform();
}

export async function checkBiometricAvailability() {
  if (!canUseNativeBiometrics()) {
    return { ok: false, available: false, reason: 'Biometrics are only enabled in the native app' };
  }

  try {
    const info = await BiometricAuth.checkBiometry();
    return {
      ok: true,
      available: Boolean(info?.isAvailable),
      info,
      reason: info?.reason || '',
    };
  } catch (err) {
    return {
      ok: false,
      available: false,
      reason: err?.message || 'Unable to check biometric availability',
    };
  }
}

export async function authenticateWithBiometrics() {
  if (!canUseNativeBiometrics()) {
    return { ok: false, reason: 'Biometrics are only enabled in the native app' };
  }

  try {
    await BiometricAuth.authenticate({
      reason: 'Unlock Qureo',
      cancelTitle: 'Cancel',
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'Unlock Qureo',
      androidSubtitle: 'Use biometrics to continue',
      androidConfirmationRequired: false,
      allowDeviceCredential: true,
    });

    setBiometricSessionUnlocked(true);
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: err?.message || 'Biometric authentication failed',
      code: err?.code,
    };
  }
}
